import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InvoiceForm from "./InvoiceForm.jsx";
import { API, graphqlOperation } from "aws-amplify";
import { getUser } from "../../graphql/queries";
import { getPricelist } from "../../graphql/queries";
import { useInvoice } from "../../contexts/InvoiceContext";
import { fetchUserById } from "../../services/userManagementService";

const InvoiceCreateModal = ({
  open,
  onClose,
  customerId,
  customerName,
  customerData: propCustomerData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pricelistData, setPricelistData] = useState(null);
  const [customerRecord, setCustomerRecord] = useState(null);
  const { setCurrentCustomer, setCurrentPricelistName } = useInvoice();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSuccess(false);
      setIsSubmitting(false);
    }
  }, [open]);

  // Fetch full customer record when modal opens
  useEffect(() => {
    const fetchCustomerRecord = async () => {
      if (open && customerId) {
        try {
          const customer = await fetchUserById(customerId);
          if (customer) {
            setCustomerRecord(customer);
            setCurrentCustomer(customer);
            setCurrentPricelistName(
              customer.activePricelist?.name ||
                customer.activePricelistID ||
                "Default Pricelist",
            );
            setPricelistData({
              id: customer.activePricelistID,
              name: customer.activePricelist?.name || "Pricelist",
            });
            console.log(
              "InvoiceCreateModal - Customer record loaded:",
              customer,
            );
          }
        } catch (error) {
          console.error("Error fetching customer record:", error);
        }
      } else if (open && propCustomerData) {
        // Fallback to propCustomerData if customerId is not available
        setCustomerRecord(propCustomerData);
        setCurrentCustomer(propCustomerData);
        setCurrentPricelistName(
          propCustomerData.activePricelist?.name ||
            propCustomerData.activePricelistID ||
            "Default Pricelist",
        );
        setPricelistData({
          id: propCustomerData.activePricelistID,
          name: propCustomerData.activePricelist?.name || "Pricelist",
        });
        console.log(
          "InvoiceCreateModal - Using prop customer:",
          propCustomerData,
        );
      }
    };

    fetchCustomerRecord();
  }, [open, customerId, propCustomerData]);

  const handleSuccess = (data) => {
    setSuccess(true);
    setIsSubmitting(false);
    console.log("Invoice created successfully:", data);
  };

  const handleError = (data, messages) => {
    setIsSubmitting(false);
    console.error("Invoice creation error:", messages);
  };

  const handleSubmit = (data) => {
    setIsSubmitting(true);
    return data;
  };

  const handleDialogClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#000000",
          color: "#32FF32",
          border: "1px solid #32FF32",
          width: "90%",
          maxWidth: "1200px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#1a1a1a",
          borderBottom: "1px solid #32FF32",
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#32FF32",
              ml: 1,
            }}
          >
            Create Invoice
          </Typography>
        </Box>
        <IconButton
          onClick={handleDialogClose}
          sx={{
            color: "#32FF32",
            "&:hover": {
              backgroundColor: "rgba(50, 255, 50, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          backgroundColor: "#000000",
          pt: 2,
        }}
      >
        {success ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#32FF32",
                mb: 2,
                fontWeight: 600,
              }}
            >
              Invoice Created Successfully!
            </Typography>
            <Typography variant="body2" sx={{ color: "#D3D3D3", mb: 3 }}>
              {customerName && `Customer: ${customerName}`}
            </Typography>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                bgcolor: "#32FF32",
                color: "#000",
                fontWeight: 600,
                px: 3,
                py: 1,
                "&:hover": {
                  bgcolor: "#2ad62a",
                },
              }}
            >
              Close
            </Button>
          </Box>
        ) : (
          <Box sx={{ px: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: "#D3D3D3",
                mb: 2,
                fontStyle: "italic",
              }}
            >
              {customerRecord ? (
                <span>
                  Customer:{" "}
                  {customerRecord.companyName ||
                    customerRecord.email ||
                    "Unknown Customer"}
                </span>
              ) : propCustomerData ? (
                <span>
                  Customer:{" "}
                  {propCustomerData.companyName ||
                    propCustomerData.email ||
                    "Unknown Customer"}
                </span>
              ) : (
                <span>{customerName && `Customer: ${customerName}`}</span>
              )}
            </Typography>
            <InvoiceForm
              customerData={customerRecord || propCustomerData}
              pricelistData={pricelistData}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions
          sx={{
            backgroundColor: "#1a1a1a",
            borderTop: "1px solid #32FF32",
            pt: 1,
          }}
        >
          <Button
            onClick={handleDialogClose}
            disabled={isSubmitting}
            sx={{
              color: "#D3D3D3",
              "&:hover": {
                backgroundColor: "rgba(211, 211, 211, 0.1)",
              },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default InvoiceCreateModal;
